import api from './api'

// Get a presigned URL for uploading an import file
export const getUploadUrl = async (fileName, contentType, fileSize) => {
  const response = await api.post('/imports/upload-url', {
    fileName,
    contentType,
    fileSize
  })
  return response.data
}

// Upload file directly to S3 using presigned URL.
// contentType must match the value used when getUploadUrl was called — the
// presigned URL is signed against it and S3 will reject a mismatched header.
export const uploadFile = async (uploadUrl, file, contentType) => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType || file.type
    },
    body: file
  })

  if (!response.ok) {
    throw new Error('Failed to upload file to S3')
  }

  return { success: true }
}

// Parse an uploaded file to extract data for mapping
export const parseFile = async (fileKey) => {
  const response = await api.post('/imports/parse', { fileKey })
  return response.data
}

// Start an import job
export const startImport = async (data) => {
  const response = await api.post('/imports', data)
  return response.data
}

// Get import status/details by ID
export const getImport = async (importId) => {
  const response = await api.get(`/imports/${importId}`)
  return response.data
}

// List imports with optional filters
export const listImports = async ({ cursor, limit = 20, type, status } = {}) => {
  const params = new URLSearchParams()
  if (cursor) params.append('cursor', JSON.stringify(cursor))
  if (limit) params.append('limit', limit.toString())
  if (type) params.append('type', type)
  if (status) params.append('status', status)

  const response = await api.get(`/imports?${params.toString()}`)
  return response.data
}

// Download failed records as CSV
export const downloadFailedRecords = async (importId) => {
  const response = await api.get(`/imports/${importId}/failed-records`, {
    responseType: 'blob',
  })
  return response
}

// Get failed records with full detail for inline editing
export const getFailedRecordsDetail = async (importId, { limit = 50, offset = 0 } = {}) => {
  const params = new URLSearchParams()
  if (limit) params.append('limit', limit.toString())
  if (offset) params.append('offset', offset.toString())

  const response = await api.get(`/imports/${importId}/failed-records/detail?${params.toString()}`)
  return response.data
}

// Retry failed records with corrected data
export const retryFailedRecords = async (importId, records, sendInvitations = false) => {
  const response = await api.post(`/imports/${importId}/retry`, { records, sendInvitations })
  return response.data
}

// Download a per-form bulk-import template (CSV or JSON) for a submissions import.
// Hits GET /forms/{formIdOrSlug}/import-template?format=csv|json, which returns a
// file body (text/csv or application/json) with a Content-Disposition header.
// Requested as a blob so the caller can trigger a browser download. This only
// drives template generation — the actual import still keys off the per-row
// `formSlug` column, not the picked form.
export const getImportTemplate = async (formIdOrSlug, format = 'csv') => {
  const response = await api.get(
    `/forms/${encodeURIComponent(formIdOrSlug)}/import-template?format=${format}`,
    { responseType: 'blob' }
  )
  return response
}

// Mark an import as failed (admin only)
export const markImportFailed = async (importId, reason) => {
  const response = await api.post(`/imports/${importId}/mark-failed`, { reason })
  return response.data
}

// Start a Glacier retrieval of an import's original uploaded file (#84)
export const restoreImportFile = async (importId) => {
  const response = await api.post(`/imports/${importId}/restore`)
  return response.data
}

// Get a presigned download URL for an import's original uploaded file (#84)
export const getImportDownloadUrl = async (importId) => {
  const response = await api.get(`/imports/${importId}/download`)
  return response.data
}

// ---------------------------------------------------------------------------
// Scheduled/recurring imports (#82)
// ---------------------------------------------------------------------------

// List the tenant's import schedules (capped at 10 — no pagination needed).
export const listSchedules = async () => {
  const response = await api.get('/imports/schedules')
  return response.data
}

// Get a single schedule by ID.
export const getSchedule = async (scheduleId) => {
  const response = await api.get(`/imports/schedules/${scheduleId}`)
  return response.data
}

// Create a new import schedule.
export const createSchedule = async (data) => {
  const response = await api.post('/imports/schedules', data)
  return response.data
}

// Update editable fields on a schedule (name, fieldMappings, options, cadence, notifyEmails).
export const updateSchedule = async (scheduleId, data) => {
  const response = await api.put(`/imports/schedules/${scheduleId}`, data)
  return response.data
}

// Activate (enable) a schedule — re-enters the due-queue with a freshly computed nextRunAt.
export const activateSchedule = async (scheduleId) => {
  const response = await api.post(`/imports/schedules/${scheduleId}/activate`)
  return response.data
}

// Deactivate (disable) a schedule — drops out of the due-queue entirely.
export const deactivateSchedule = async (scheduleId) => {
  const response = await api.post(`/imports/schedules/${scheduleId}/deactivate`)
  return response.data
}

// Hard-delete a schedule (and its fixed S3 source key, server-side).
export const deleteSchedule = async (scheduleId) => {
  const response = await api.delete(`/imports/schedules/${scheduleId}`)
  return response.data
}

// Presign a PUT for the schedule's fixed S3 key — never a caller-supplied one.
export const getScheduleUploadUrl = async (scheduleId) => {
  const response = await api.post(`/imports/schedules/${scheduleId}/upload-url`, {})
  return response.data
}

// "Run now" — forces a run through the same pipeline the hourly sweeper uses.
// Does not advance the schedule's nextRunAt.
export const runScheduleNow = async (scheduleId) => {
  const response = await api.post(`/imports/schedules/${scheduleId}/run`)
  return response.data
}

export default {
  getUploadUrl,
  uploadFile,
  parseFile,
  startImport,
  getImport,
  listImports,
  downloadFailedRecords,
  getFailedRecordsDetail,
  retryFailedRecords,
  markImportFailed,
  getImportTemplate,
  restoreImportFile,
  getImportDownloadUrl,
  listSchedules,
  getSchedule,
  createSchedule,
  updateSchedule,
  activateSchedule,
  deactivateSchedule,
  deleteSchedule,
  getScheduleUploadUrl,
  runScheduleNow
}
